import { useState, createRef, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import Button from '../../../components/Button';
import styled, { useTheme } from 'styled-components';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const DropzoneContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 30px;
  border: 1px dashed ${({ theme }) => theme.text.secondary};
  border-radius: 8px;
  cursor: pointer;
  width: 100%;
  margin-bottom: 10px;
  height: 200px;
  text-align: center;
`;

const Label = styled.label`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.text.secondary};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const UploadImage = styled.img`
  width: 53px;
  padding-bottom: 10px;
`;

const CloseIconCointainer = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const Icon = styled.img`
  width: 20px;
  height: 20px;
`;

const ImageInput = ({
  value,
  onChange,
  label,
  aspectRatio = 1.8,
  circularCrop = false,
}) => {
  const theme = useTheme();
  const [image, setImageState] = useState(null);
  const [crop, setCrop] = useState();
  const [showCropper, setShowCropper] = useState(false);
  const [completedDrop, setCompletedCrop] = useState(null);
  const [completedDropImage, setCompletedDropImage] = useState(null);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageData = reader.result;
        setImageState(imageData);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    onDrop,
  });

  useEffect(() => {
    (async () => {
      if (
        imgRef.current &&
        canvasRef.current &&
        crop &&
        crop.width &&
        crop.height
      ) {
        const canvas = canvasRef.current;
        const image = imgRef.current;
        const ctx = canvas.getContext('2d');
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const pixelRatio = window.devicePixelRatio;
        const cropWidth = Math.floor(crop.width * scaleX);
        const cropHeight = Math.floor(crop.height * scaleY);
        const cropX = crop.x * scaleX;
        const cropY = crop.y * scaleY;

        canvas.width = cropWidth * pixelRatio;
        canvas.height = cropHeight * pixelRatio;
        ctx.scale(pixelRatio, pixelRatio);
        ctx.imageSmoothingQuality = 'high';

        if (circularCrop && ctx) {
          const radius = Math.min(cropWidth, cropHeight) / 2;
          ctx.beginPath();
          ctx.arc(cropWidth / 2, cropHeight / 2, radius, 0, Math.PI * 2, true);
          ctx.closePath();
          ctx.clip();
        }

        ctx.drawImage(
          image,
          cropX,
          cropY,
          cropWidth,
          cropHeight,
          0,
          0,
          cropWidth,
          cropHeight,
        );

        const dataUrl = canvas.toDataURL('image/png', 0.95);
        setCompletedDropImage(dataUrl);
      }
    })();
  }, [completedDrop, crop, circularCrop]);

  const getCenteredAspectCrop = (mediaWidth, mediaHeight, aspect) => {
    const mediaAspect = mediaWidth / mediaHeight;
    let cropWidth, cropHeight;

    if (mediaAspect > aspect) {
      cropHeight = mediaHeight;
      cropWidth = cropHeight * aspect;
    } else {
      cropWidth = mediaWidth;
      cropHeight = cropWidth / aspect;
    }

    const x = (mediaWidth - cropWidth) / 2;
    const y = (mediaHeight - cropHeight) / 2;

    return {
      unit: 'px',
      width: cropWidth,
      height: cropHeight,
      x,
      y,
    };
  };

  const getCropData = () => {
    if (completedDropImage) {
      setImageState(null);
      setShowCropper(false);
      onChange(completedDropImage);
    }
  };

  if (value) {
    return (
      <div
        style={{
          position: 'relative',
        }}
      >
        <CloseIconCointainer onClick={() => onChange(null)}>
          <Icon src='/close.webp' />
        </CloseIconCointainer>
        <img src={value} width={'100%'} />
      </div>
    );
  }

  return (
    <div>
      <Label>{label}</Label>
      {!showCropper ? (
        <>
          <DropzoneContainer {...getRootProps()}>
            <UploadImage src='/upload.webp' />
            <input {...getInputProps()} />
            <p
              style={{
                fontSize: 14,
              }}
            >
              Drag image here or{' '}
              <span
                style={{
                  color: `${theme.primary.primary}`,
                  fontWeight: 600,
                }}
              >
                click to upload
              </span>
            </p>
          </DropzoneContainer>
          <Label>jpg/png files with a size less than 1MB.</Label>
        </>
      ) : (
        <>
          <ReactCrop
            crop={crop}
            onChange={(newCrop) => setCrop(newCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            circularCrop={circularCrop}
            aspect={aspectRatio}
          >
            <img
              ref={imgRef}
              src={image}
              alt='Crop me'
              onLoad={(e) => {
                const { width, height } = e.currentTarget;
                const newCrop = getCenteredAspectCrop(
                  width,
                  height,
                  aspectRatio,
                );
                setCrop(newCrop);
                setCompletedCrop(newCrop);
              }}
            />
          </ReactCrop>
          <ButtonContainer>
            <Button variant='secondary' onClick={() => setShowCropper(false)}>
              Cancel
            </Button>
            <Button variant='primary' onClick={getCropData}>
              Crop & Save
            </Button>
          </ButtonContainer>
        </>
      )}
      {completedDrop && (
        <canvas
          ref={canvasRef}
          style={{
            display: 'none',
          }}
        />
      )}
    </div>
  );
};

export default ImageInput;
